import { Box, Button, Card } from '@palmetto/palmetto-components';
import { DateTime } from 'luxon';
import { useNavigate, useParams } from 'react-router-dom';

import { IncentiveSubtypeLabels, ProgramTypeLabels } from 'types';

import { useGetIncentiveQuery } from '../../services/incentives';
import { PropertyValue } from '../PropertyListBox/PropertyValue';
import { SkeletonBox } from '../SkeletonBox';

import { AllocationsSidebar } from './AllocationsSidebar';
import { IncentiveAccountsTable } from './IncentiveAccountsTable';

const convertIsoToDateMed = (isoDate: string | Date | undefined) => {
  if (!isoDate) return null;
  const dateTime = typeof isoDate === 'string' ? DateTime.fromISO(isoDate) : DateTime.fromJSDate(isoDate);
  return dateTime.toLocaleString(DateTime.DATE_MED);
};

export function IncentiveOverview() {
  const { key } = useParams<{ key: string }>();
  const navigate = useNavigate();
  const { data: incentive, isFetching } = useGetIncentiveQuery({ key: key! });

  const programTypesDisplay = incentive?.programTypes?.length
    ? incentive.programTypes.map((pt) => ProgramTypeLabels[pt]).join(', ')
    : 'N/A';

  const subtypeDisplay =
    incentive?.subtype && IncentiveSubtypeLabels[incentive.subtype]
      ? IncentiveSubtypeLabels[incentive.subtype]
      : 'N/A';

  const handleAllocateAccounts = () => {
    navigate(`/admin/incentives/${key}/allocate`);
  };

  const handleBlockAccounts = () => {
    navigate(`/admin/incentives/${key}/block`);
  };

  const handleUnallocateAccounts = () => {
    navigate(`/admin/incentives/${key}/unallocate`);
  };

  return (
    <Box direction="row" childGap="3xl" padding="lg 0">
      {/* Left Sidebar */}
      <Box width="280px" style={{ flexShrink: 0 }}>
        <Box margin="0 0 lg 0" childGap="xs">
          <Button size="sm" isLoading={isFetching} onClick={handleAllocateAccounts}>
            Allocate Accounts
          </Button>
          <Button size="sm" variant="secondary" isLoading={isFetching} onClick={handleUnallocateAccounts}>
            Un-Allocate Accounts
          </Button>
          <Button size="sm" variant="primary" tone="danger" isLoading={isFetching} onClick={handleBlockAccounts}>
            Block Accounts
          </Button>
        </Box>
        <Box childGap="lg" radius="md" borderWidth="xs" borderColor="separator" padding="lg" margin="0 0 lg 0">
          <Box as="h3" fontWeight="medium" fontSize="md">
            Incentive Details
          </Box>
          {isFetching ? (
            <Box childGap="xs" fontSize="xs">
              <SkeletonBox height="16px" width="100%" />
              <SkeletonBox height="16px" width="100%" />
              <SkeletonBox height="16px" width="100%" />
              <SkeletonBox height="16px" width="100%" />
            </Box>
          ) : (
            <Box childGap="xs" fontSize="xs">
              <PropertyValue label="Salesforce ID" value={incentive?.salesforceId || 'N/A'} />
              <PropertyValue
                label="Expiration Date"
                value={convertIsoToDateMed(incentive?.expirationDate) || 'N/A'}
              />
              <PropertyValue label="Incentive Subtype" value={subtypeDisplay} />
              <PropertyValue label="Lightreach Program Type" value={programTypesDisplay} />
            </Box>
          )}
        </Box>

        <AllocationsSidebar allocations={incentive?.allocations} isLoading={isFetching} />
      </Box>

      {/* Main Content */}
      <Box flex="auto">
        <Card padding="lg">
          <IncentiveAccountsTable />
        </Card>
      </Box>
    </Box>
  );
}
